// ─────────────────────────────────────────────────────────────────────────────
// Dates and times on screen — the one hook every screen formats through.
//
// It binds the two things a component would otherwise have to remember at each
// call site, and used to get wrong:
//
//   1. THE FORMAT. `YYYY-MM-DD`, `HH:MM`, and `YYYY-MM-DD HH:MM` — decided in
//      `@shared/dates`, which explains why. Every screen used to call
//      `toLocaleDateString` with its own options, so four screens showed four
//      formats and the order flipped when the language did.
//
//   2. THE ZONE. The BRANCH's, from `workspace.navigation`, not the browser's.
//      Attendance, payroll and the calendar are computed in the branch's zone;
//      a punch shown in the machine's zone is a punch on the wrong day for
//      anybody who has travelled, and the disagreement arrives as a support
//      call rather than as a bug report.
//
// It costs no request: `workspace.navigation` is the query the shell already
// makes, and React Query hands back the same cached answer.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from "react";
import {
  DEFAULT_TIMEZONE,
  formatDay, formatDayWithWeekday,
  formatZonedClock, formatZonedDate, formatZonedDateTime, formatZonedTime,
  monthLabel, monthName,
  type IsoDate,
} from "@shared/dates";
import { trpc } from "../api";
import { useI18n } from "./index";

/** An instant as it arrives from the API, or nothing. */
type Instant = Date | string | number | null | undefined;

export interface Formatter {
  /** The zone every time below is in — the branch's, or the clinic's default. */
  readonly timezone: string;
  /** A calendar day: `2026-09-08`. */
  readonly day: (date: IsoDate | null | undefined) => string;
  /** A calendar day with its weekday, for a heading: `Sunday, 2026-09-08`. */
  readonly dayLong: (date: IsoDate) => string;
  /** An instant as the calendar day it falls on in the branch's zone. */
  readonly date: (at: Instant) => string;
  /** An instant as branch wall-clock time, for a column: `14:32`. */
  readonly time: (at: Instant) => string;
  /** The same instant the way a watch reads it: `3:40 PM` / `3:40 م`. */
  readonly clock: (at: Instant) => string;
  /** An instant as `2026-09-08 14:32`, in the branch's zone. */
  readonly dateTime: (at: Instant) => string;
  /** A month by name in the current language: `August`. */
  readonly month: (month: number) => string;
  /** A period by name in the current language: `August 2026`. */
  readonly period: (year: number, month: number) => string;
}

export function useFormat(): Formatter {
  const { locale } = useI18n();
  // Same options as the shell's own call, so the two share one cache entry.
  const navigation = trpc.workspace.navigation.useQuery(undefined, { staleTime: 60_000, retry: false });
  const timezone = navigation.data?.timezone ?? DEFAULT_TIMEZONE;

  return useMemo<Formatter>(() => ({
    timezone,
    day: (date) => formatDay(date),
    dayLong: (date) => formatDayWithWeekday(date, locale),
    date: (at) => (at === null || at === undefined ? "—" : formatZonedDate(at, timezone)),
    time: (at) => (at === null || at === undefined ? "—" : formatZonedTime(at, timezone)),
    clock: (at) => (at === null || at === undefined ? "—" : formatZonedClock(at, timezone, locale)),
    dateTime: (at) => (at === null || at === undefined ? "—" : formatZonedDateTime(at, timezone)),
    month: (month) => monthName(month, locale),
    period: (year, month) => monthLabel(year, month, locale),
  }), [timezone, locale]);
}
